// Client certificate vault — accreditation + company certificates with expiry
// countdown + AI auto-detection on upload. Internal staff pick a client; portal
// users are scoped to their own. Edit = director/admin/consultant/client_admin.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    BadgeCheck, Upload, Plus, Pencil, Trash2, Download, Sparkles, X, FileText, Loader2, Paperclip,
    ArrowUp, ArrowDown, ChevronsUpDown,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useView } from "@/context/ViewContext";
import { toast } from "sonner";
import CertBulkImport, { BULK_LIMIT } from "./CertBulkImport";
import CertBulkAttach from "./CertBulkAttach";

const EDIT_ROLES = new Set(["director", "administrator", "consultant", "client_admin"]);
const ACCENT = "var(--brand-accent, #C75A1B)";

const STATUS_STYLE = {
    expired: { bg: "#FBE8E5", fg: "#B33A2E", label: "Expired" },
    critical: { bg: "#FBE8E5", fg: "#B33A2E", label: "Expiring soon" },
    warning: { bg: "#FCF1DC", fg: "#C68A1C", label: "Renew soon" },
    ok: { bg: "#E8F1E6", fg: "#3F7D3A", label: "Valid" },
    none: { bg: "#F0EDE8", fg: "#6F6A64", label: "No expiry" },
};

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso + "T00:00:00").toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    } catch { return iso; }
}

function ExpiryBadge({ cert }) {
    const s = STATUS_STYLE[cert.expiry_status] || STATUS_STYLE.none;
    const d = cert.days_to_expiry;
    let text = s.label;
    if (cert.expiry_status === "expired" && d != null) text = `Expired ${Math.abs(d)}d ago`;
    else if (d != null && cert.expiry_status !== "none") text = `${d}d left`;
    return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold"
            style={{ background: s.bg, color: s.fg }} data-testid={`cert-expiry-${cert.id}`}>
            {text}
        </span>
    );
}

function SortableTh({ label, col, sort, onSort, className = "" }) {
    const active = sort.key === col;
    const Icon = active ? (sort.dir === "asc" ? ArrowUp : ArrowDown) : ChevronsUpDown;
    return (
        <th className={`text-left ${className || "px-3"} py-2.5 font-bold`}>
            <button
                type="button"
                data-testid={`cert-sort-${col}`}
                onClick={() => onSort(col)}
                className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-[#1F1F1F] transition-colors"
                style={{ color: active ? "#1F1F1F" : "inherit" }}
            >
                {label}
                <Icon className="w-3 h-3" style={{ opacity: active ? 1 : 0.4 }} />
            </button>
        </th>
    );
}

const EMPTY = {
    category: "accreditation", title: "", issuing_body: "", certificate_number: "",
    issued_date: "", expiry_date: "", has_expiry: true, scope: "", notes: "", file: null,
};

export default function CertificatesPage() {
    const { user } = useAuth();
    const { portalClient, isInternalView, clients } = useView();
    const canEdit = user && EDIT_ROLES.has(user.role);

    const [pickedClient, setPickedClient] = useState("");
    const clientId = isInternalView ? pickedClient : portalClient?.id;

    const [tab, setTab] = useState("accreditation");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [scanning, setScanning] = useState(false);
    const [editor, setEditor] = useState({ open: false, mode: "create", id: null, draft: EMPTY, confidence: null });
    const [bulk, setBulk] = useState({ open: false, files: [] });
    const [attach, setAttach] = useState({ open: false, files: [] });
    const [selected, setSelected] = useState(() => new Set());
    const [bulkDeleting, setBulkDeleting] = useState(false);
    const fileRef = useRef(null);
    const bulkRef = useRef(null);
    const attachRef = useRef(null);

    useEffect(() => {
        if (isInternalView && !pickedClient && clients.length) setPickedClient(clients[0].id);
    }, [isInternalView, clients, pickedClient]);

    const refresh = useCallback(async () => {
        if (!clientId) { setItems([]); return; }
        setLoading(true);
        try {
            const { data } = await api.get("/certificates", { params: { client_id: clientId } });
            setItems(data);
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
        finally { setLoading(false); }
    }, [clientId]);

    useEffect(() => { refresh(); }, [refresh]);
    useEffect(() => { setSelected(new Set()); }, [clientId, tab]);

    const filtered = useMemo(() => items.filter((c) => c.category === tab), [items, tab]);

    // Sorting — default by Status: expired first, then fewest days to expiry; no-expiry last.
    const [sort, setSort] = useState({ key: "status", dir: "asc" });
    const toggleSort = (key) => setSort((s) => (
        s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }
    ));
    const sorted = useMemo(() => {
        const dir = sort.dir === "asc" ? 1 : -1;
        const str = (v) => (v || "").toString().toLowerCase();
        const dayRank = (c) => {
            const d = c.days_to_expiry;
            if (d == null || !c.has_expiry) return Number.POSITIVE_INFINITY; // no-expiry always last
            return d;
        };
        const cmp = (a, b) => {
            switch (sort.key) {
                case "title": return str(a.title).localeCompare(str(b.title));
                case "issuing_body": return str(a.issuing_body).localeCompare(str(b.issuing_body));
                case "certificate_number": return str(a.certificate_number).localeCompare(str(b.certificate_number));
                case "issued_date": return str(a.issued_date).localeCompare(str(b.issued_date));
                case "expiry_date":
                case "status": {
                    const ra = dayRank(a), rb = dayRank(b);
                    if (ra === rb) return str(a.title).localeCompare(str(b.title));
                    return ra < rb ? -1 : 1;
                }
                default: return 0;
            }
        };
        // No-expiry rows stay last regardless of asc/desc when sorting by status/expiry.
        const arr = [...filtered];
        if (sort.key === "status" || sort.key === "expiry_date") {
            const withExp = arr.filter((c) => c.has_expiry && c.days_to_expiry != null);
            const noExp = arr.filter((c) => !(c.has_expiry && c.days_to_expiry != null));
            withExp.sort((a, b) => cmp(a, b) * dir);
            noExp.sort((a, b) => str(a.title).localeCompare(str(b.title)));
            return [...withExp, ...noExp];
        }
        arr.sort((a, b) => cmp(a, b) * dir);
        return arr;
    }, [filtered, sort]);
    const counts = useMemo(() => ({
        accreditation: items.filter((c) => c.category === "accreditation").length,
        company: items.filter((c) => c.category === "company").length,
    }), [items]);

    async function onUploadAuto(e) {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f || !clientId) return;
        const fd = new FormData();
        fd.append("file", f);
        fd.append("client_id", clientId);
        setScanning(true);
        try {
            const { data } = await api.post("/certificates/ai-extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
            const d = data.draft || {};
            setEditor({
                open: true, mode: "create", id: null, confidence: d.confidence ?? null,
                draft: {
                    ...EMPTY,
                    category: d.category || (tab === "company" ? "company" : "accreditation"),
                    title: d.title || "", issuing_body: d.issuing_body || "",
                    certificate_number: d.certificate_number || "",
                    issued_date: d.issued_date || "", expiry_date: d.expiry_date || "",
                    has_expiry: d.has_expiry !== false && !!d.expiry_date,
                    scope: d.scope || "", file: data.file, ai_extracted: true,
                },
            });
            toast.success("Details auto-detected — please review before saving.");
        } catch (e2) { toast.error(formatApiError(e2?.response?.data?.detail) || "Auto-detect failed"); }
        finally { setScanning(false); }
    }

    function onBulkFiles(e) {
        const files = Array.from(e.target.files || []);
        e.target.value = "";
        if (!files.length || !clientId) return;
        if (files.length > BULK_LIMIT) {
            toast.error(`Please select up to ${BULK_LIMIT} files at a time. Using the first ${BULK_LIMIT}.`);
        }
        setBulk({ open: true, files: files.slice(0, BULK_LIMIT) });
    }

    function onAttachFiles(e) {
        const files = Array.from(e.target.files || []);
        e.target.value = "";
        if (!files.length || !clientId) return;
        setAttach({ open: true, files });
    }

    const toggleSelect = (id) => setSelected((prev) => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });
    const allVisibleSelected = filtered.length > 0 && filtered.every((c) => selected.has(c.id));
    const toggleSelectAll = () => setSelected((prev) => {
        const next = new Set(prev);
        if (allVisibleSelected) filtered.forEach((c) => next.delete(c.id));
        else filtered.forEach((c) => next.add(c.id));
        return next;
    });

    async function onBulkDelete() {
        const ids = filtered.filter((c) => selected.has(c.id)).map((c) => c.id);
        if (!ids.length) return;
        if (!window.confirm(`Delete ${ids.length} certificate${ids.length > 1 ? "s" : ""}? This cannot be undone.`)) return;
        setBulkDeleting(true);
        try {
            await api.post("/certificates/bulk-delete", { ids });
            toast.success(`${ids.length} certificate${ids.length > 1 ? "s" : ""} deleted`);
            setSelected(new Set());
            refresh();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
        finally { setBulkDeleting(false); }
    }

    function openManual() {        setEditor({ open: true, mode: "create", id: null, confidence: null, draft: { ...EMPTY, category: tab } });
    }
    function openEdit(c) {
        setEditor({ open: true, mode: "edit", id: c.id, confidence: null, draft: { ...EMPTY, ...c, file: c.file || null } });
    }

    async function onDelete(c) {
        if (!window.confirm(`Delete "${c.title}"? This cannot be undone.`)) return;
        try {
            await api.delete(`/certificates/${c.id}`);
            toast.success("Certificate deleted");
            refresh();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    }

    // Fetch the file through axios (carries credentials + auto token-refresh) and
    // open it — a raw <a href> can silently 401 on a stale cookie / cross-origin.
    async function viewFile(c) {
        if (!c.file?.storage_path) return;
        const w = window.open("", "_blank");   // opened synchronously to dodge popup blockers
        try {
            const resp = await api.get(`/certificates/${c.id}/file`, { responseType: "blob" });
            const url = URL.createObjectURL(resp.data);
            if (w) { w.location = url; }
            else {
                const a = document.createElement("a");
                a.href = url; a.download = c.file.file_name || "certificate";
                document.body.appendChild(a); a.click(); a.remove();
            }
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (e) {
            if (w) w.close();
            toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't open the file");
        }
    }

    const noClient = !clientId;

    return (
        <div data-testid="certificates-page" className="space-y-5">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <span className="inline-block pb-1 text-[10.5px] font-bold tracking-[.08em] uppercase"
                        style={{ borderBottom: `2px solid ${ACCENT}`, color: "#6F6A64" }}>
                        Compliance
                    </span>
                    <h2 className="text-2xl font-bold tracking-tight mt-2 text-[#1F1F1F] flex items-center gap-2">
                        <BadgeCheck className="w-6 h-6" style={{ color: ACCENT }} /> Certificates
                    </h2>
                    <p className="text-[13px] mt-1" style={{ color: "#6F6A64" }}>
                        Accreditation &amp; company certificates with expiry tracking and AI auto-detection.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {isInternalView && (
                        <select
                            data-testid="cert-client-select"
                            value={pickedClient}
                            onChange={(e) => setPickedClient(e.target.value)}
                            className="bg-white border rounded-[8px] px-3 py-2 text-[12.5px] font-semibold text-[#1F1F1F] outline-none"
                            style={{ borderColor: "#E5E0DA" }}
                        >
                            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    )}
                    {canEdit && !noClient && (
                        <>
                            <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/*"
                                className="hidden" data-testid="cert-upload-input" onChange={onUploadAuto} />
                            <input ref={bulkRef} type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/*"
                                className="hidden" data-testid="cert-bulk-input" onChange={onBulkFiles} />
                            <input ref={attachRef} type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/*"
                                className="hidden" data-testid="cert-attach-files-input" onChange={onAttachFiles} />
                            <button
                                data-testid="cert-upload-button"
                                onClick={() => fileRef.current?.click()}
                                disabled={scanning}
                                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[8px] text-[12.5px] font-semibold text-white transition-colors disabled:opacity-60"
                                style={{ background: ACCENT }}
                            >
                                {scanning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                                {scanning ? "Reading…" : "Upload & auto-detect"}
                            </button>
                            <button
                                data-testid="cert-bulk-button"
                                onClick={() => bulkRef.current?.click()}
                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[8px] text-[12.5px] font-semibold border transition-colors hover:bg-[#F6F4F0]"
                                style={{ borderColor: "#E5E0DA", color: "#2D2D2D" }}
                            >
                                <Sparkles className="w-3.5 h-3.5" style={{ color: ACCENT }} /> Bulk import
                            </button>
                            <button
                                data-testid="cert-attach-files-button"
                                onClick={() => attachRef.current?.click()}
                                title="Attach real documents to existing certificates"
                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[8px] text-[12.5px] font-semibold border transition-colors hover:bg-[#F6F4F0]"
                                style={{ borderColor: "#E5E0DA", color: "#2D2D2D" }}
                            >
                                <Paperclip className="w-3.5 h-3.5" style={{ color: ACCENT }} /> Attach files
                            </button>
                            <button
                                data-testid="cert-add-manual-button"
                                onClick={openManual}
                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[8px] text-[12.5px] font-semibold border transition-colors hover:bg-[#F6F4F0]"
                                style={{ borderColor: "#E5E0DA", color: "#2D2D2D" }}
                            >
                                <Plus className="w-3.5 h-3.5" /> Add manually
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-1 border-b" style={{ borderColor: "#E5E0DA" }}>
                {[["accreditation", "Accreditation certificates"], ["company", "Company certificates"]].map(([k, lbl]) => (
                    <button
                        key={k}
                        data-testid={`cert-tab-${k}`}
                        onClick={() => setTab(k)}
                        className="px-4 py-2 text-[12.5px] font-semibold relative -mb-px"
                        style={{
                            color: tab === k ? ACCENT : "#6F6A64",
                            borderBottom: tab === k ? `2px solid ${ACCENT}` : "2px solid transparent",
                        }}
                    >
                        {lbl} <span className="ml-1 text-[11px] opacity-70">({counts[k]})</span>
                    </button>
                ))}
            </div>

            {canEdit && selected.size > 0 && (
                <div data-testid="cert-bulk-toolbar" className="flex items-center justify-between gap-3 px-4 py-2.5 rounded-[10px] border" style={{ borderColor: "#E5E0DA", background: "#FBF6F1" }}>
                    <span className="text-[12.5px] font-semibold text-[#1F1F1F]">{selected.size} selected</span>
                    <div className="flex items-center gap-2">
                        <button onClick={() => setSelected(new Set())} className="px-3 py-1.5 rounded-[7px] text-[12px] font-semibold border hover:bg-white" style={{ borderColor: "#E5E0DA" }}>Clear</button>
                        <button data-testid="cert-bulk-delete-button" onClick={onBulkDelete} disabled={bulkDeleting}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[7px] text-[12px] font-semibold text-white disabled:opacity-60" style={{ background: "#B33A2E" }}>
                            {bulkDeleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} Delete selected
                        </button>
                    </div>
                </div>
            )}

            {noClient ? (
                <div className="bg-white border rounded-[11px] p-10 text-center" style={{ borderColor: "#E5E0DA" }}>
                    <p className="text-[13px]" style={{ color: "#6F6A64" }}>Select a client to view their certificates.</p>
                </div>
            ) : (
                <div className="bg-white border rounded-[11px] shadow-brand-sm overflow-hidden" style={{ borderColor: "#E5E0DA" }}>
                    <table className="w-full text-[12.7px]">
                        <thead>
                            <tr className="bg-[#F6F4F0] text-[10.5px] uppercase tracking-wider" style={{ color: "#6F6A64" }}>
                                {canEdit && (
                                    <th className="px-4 py-2.5 w-9">
                                        <input type="checkbox" data-testid="cert-select-all" checked={allVisibleSelected} onChange={toggleSelectAll} aria-label="Select all" />
                                    </th>
                                )}
                                <SortableTh label="Certificate" col="title" sort={sort} onSort={toggleSort} className="px-5" />
                                <SortableTh label="Issuing body" col="issuing_body" sort={sort} onSort={toggleSort} />
                                <SortableTh label="Number" col="certificate_number" sort={sort} onSort={toggleSort} />
                                <SortableTh label="Issued" col="issued_date" sort={sort} onSort={toggleSort} />
                                <SortableTh label="Expires" col="expiry_date" sort={sort} onSort={toggleSort} />
                                <SortableTh label="Status" col="status" sort={sort} onSort={toggleSort} />
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.map((c) => (
                                <tr key={c.id} data-testid={`cert-row-${c.id}`} className="border-t hover:bg-[#FCFBF9] transition-colors" style={{ borderColor: "#F0EDE8" }}>
                                    {canEdit && (
                                        <td className="px-4 py-3">
                                            <input type="checkbox" data-testid={`cert-select-${c.id}`} checked={selected.has(c.id)} onChange={() => toggleSelect(c.id)} aria-label={`Select ${c.title}`} />
                                        </td>
                                    )}
                                    <td className="px-5 py-3">
                                        <div className="font-semibold text-[#1F1F1F] flex items-center gap-1.5">
                                            {c.title}
                                            {c.ai_extracted && <Sparkles className="w-3 h-3" style={{ color: ACCENT }} title="AI-detected" />}
                                        </div>
                                        {c.scope && <div className="text-[10.5px] mt-0.5 line-clamp-1" style={{ color: "#6F6A64" }}>{c.scope}</div>}
                                        {c.file?.storage_path ? (
                                            <button
                                                data-testid={`cert-file-name-${c.id}`}
                                                onClick={() => viewFile(c)}
                                                className="mt-1 inline-flex items-center gap-1 text-[10.5px] font-medium hover:underline"
                                                style={{ color: ACCENT }}
                                                title={`View ${c.file.file_name}`}
                                            >
                                                <Paperclip className="w-3 h-3" /> {c.file.file_name}
                                            </button>
                                        ) : (
                                            <span data-testid={`cert-no-file-${c.id}`}
                                                className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold"
                                                style={{ background: "#FCF1DC", color: "#C68A1C" }}>
                                                No file
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-3 py-3 text-[#2D2D2D]">{c.issuing_body || "—"}</td>
                                    <td className="px-3 py-3 text-[#2D2D2D]">{c.certificate_number || "—"}</td>
                                    <td className="px-3 py-3 text-[#2D2D2D]">{fmtDate(c.issued_date)}</td>
                                    <td className="px-3 py-3 text-[#2D2D2D]">{c.has_expiry ? fmtDate(c.expiry_date) : "—"}</td>
                                    <td className="px-3 py-3"><ExpiryBadge cert={c} /></td>
                                    <td className="px-3 py-3 text-right whitespace-nowrap">
                                        {c.file?.storage_path && (
                                            <button
                                                data-testid={`cert-download-${c.id}`}
                                                onClick={() => viewFile(c)}
                                                className="inline-grid place-items-center w-8 h-8 rounded-[7px] hover:bg-[#F6F4F0]"
                                                title="View / download file"
                                            >
                                                <Download className="w-4 h-4" style={{ color: "#6F6A64" }} />
                                            </button>
                                        )}
                                        {canEdit && (
                                            <>
                                                <button data-testid={`cert-edit-${c.id}`} onClick={() => openEdit(c)}
                                                    className="inline-grid place-items-center w-8 h-8 rounded-[7px] hover:bg-[#F6F4F0]" title="Edit">
                                                    <Pencil className="w-3.5 h-3.5" style={{ color: "#6F6A64" }} />
                                                </button>
                                                <button data-testid={`cert-delete-${c.id}`} onClick={() => onDelete(c)}
                                                    className="inline-grid place-items-center w-8 h-8 rounded-[7px] hover:bg-[#FBE8E5]" title="Delete">
                                                    <Trash2 className="w-3.5 h-3.5" style={{ color: "#B33A2E" }} />
                                                </button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {filtered.length === 0 && (
                                <tr><td colSpan={canEdit ? 8 : 7} className="px-5 py-10 text-center italic" style={{ color: "#9A968F" }}>
                                    {loading ? "Loading…" : `No ${tab} certificates yet.`}
                                </td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {editor.open && (
                <CertEditor
                    editor={editor}
                    clientId={clientId}
                    onClose={() => setEditor((s) => ({ ...s, open: false }))}
                    onFileAttached={refresh}
                    onSaved={() => { setEditor((s) => ({ ...s, open: false })); refresh(); }}
                />
            )}

            {bulk.open && (
                <CertBulkImport
                    files={bulk.files}
                    clientId={clientId}
                    onClose={() => setBulk({ open: false, files: [] })}
                    onSaved={() => { setBulk({ open: false, files: [] }); refresh(); }}
                />
            )}

            {attach.open && (
                <CertBulkAttach
                    files={attach.files}
                    clientId={clientId}
                    items={items}
                    onClose={() => setAttach({ open: false, files: [] })}
                    onSaved={() => { setAttach({ open: false, files: [] }); refresh(); }}
                />
            )}
        </div>
    );
}

function CertEditor({ editor, clientId, onClose, onSaved, onFileAttached }) {
    const [d, setD] = useState(editor.draft);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);
    const set = (k, v) => setD((s) => ({ ...s, [k]: v }));

    async function attachFile(e) {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f) return;
        const fd = new FormData();
        fd.append("file", f);
        setUploading(true);
        try {
            if (editor.mode === "edit" && editor.id) {
                // Persist immediately so it can't be lost by forgetting to Save.
                const { data } = await api.post(`/certificates/${editor.id}/attach`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                set("file", data.file);
                onFileAttached?.();
                toast.success("File attached & saved");
            } else {
                fd.append("client_id", clientId);
                const { data } = await api.post("/certificates/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                set("file", data.file);
                toast.success("File ready — click Save to keep it");
            }
        } catch (e2) { toast.error(formatApiError(e2?.response?.data?.detail)); }
        finally { setUploading(false); }
    }

    async function save() {
        if (!d.title?.trim()) { toast.error("Title is required"); return; }
        setSaving(true);
        try {
            const body = {
                category: d.category, title: d.title.trim(),
                issuing_body: d.issuing_body || null, certificate_number: d.certificate_number || null,
                issued_date: d.issued_date || null,
                expiry_date: d.has_expiry ? (d.expiry_date || null) : null,
                has_expiry: !!d.has_expiry, scope: d.scope || null, notes: d.notes || null,
                file: d.file || null,
            };
            if (editor.mode === "edit") {
                await api.patch(`/certificates/${editor.id}`, body);
            } else {
                await api.post("/certificates", { ...body, client_id: clientId, ai_extracted: !!d.ai_extracted });
            }
            toast.success(editor.mode === "edit" ? "Certificate updated" : "Certificate saved");
            onSaved();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
        finally { setSaving(false); }
    }

    const inputCls = "w-full bg-white border rounded-[8px] px-3 py-2 text-[12.5px] text-[#1F1F1F] outline-none focus:border-[var(--brand-accent,#C75A1B)]";
    const labelCls = "block text-[10.5px] font-bold tracking-wide uppercase mb-1";

    return (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={onClose}>
            <div data-testid="cert-editor-dialog" onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-[14px] shadow-brand w-full max-w-[560px] max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between px-5 py-3.5 border-b sticky top-0 bg-white" style={{ borderColor: "#F0EDE8" }}>
                    <div className="font-semibold text-[#1F1F1F] flex items-center gap-2">
                        {editor.mode === "edit" ? "Edit certificate" : "New certificate"}
                        {editor.confidence != null && (
                            <span className="text-[10.5px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "#FBEFE5", color: "#9F4212" }}>
                                AI {Math.round(editor.confidence * 100)}% · review
                            </span>
                        )}
                    </div>
                    <button onClick={onClose} className="w-8 h-8 grid place-items-center rounded-[7px] hover:bg-[#F6F4F0]"><X className="w-4 h-4" /></button>
                </div>

                <div className="p-5 space-y-3.5">
                    <div>
                        <label className={labelCls} style={{ color: "#6F6A64" }}>Category</label>
                        <select data-testid="cert-field-category" value={d.category} onChange={(e) => set("category", e.target.value)} className={inputCls}>
                            <option value="accreditation">Accreditation certificate</option>
                            <option value="company">Company certificate</option>
                        </select>
                    </div>
                    <div>
                        <label className={labelCls} style={{ color: "#6F6A64" }}>Title / name *</label>
                        <input data-testid="cert-field-title" value={d.title} onChange={(e) => set("title", e.target.value)} className={inputCls} placeholder="e.g. ISO 14001:2015" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className={labelCls} style={{ color: "#6F6A64" }}>Issuing body</label>
                            <input data-testid="cert-field-issuer" value={d.issuing_body || ""} onChange={(e) => set("issuing_body", e.target.value)} className={inputCls} />
                        </div>
                        <div>
                            <label className={labelCls} style={{ color: "#6F6A64" }}>Certificate number</label>
                            <input data-testid="cert-field-number" value={d.certificate_number || ""} onChange={(e) => set("certificate_number", e.target.value)} className={inputCls} />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className={labelCls} style={{ color: "#6F6A64" }}>Date issued</label>
                            <input data-testid="cert-field-issued" type="date" value={d.issued_date || ""} onChange={(e) => set("issued_date", e.target.value)} className={inputCls} />
                        </div>
                        <div>
                            <label className={labelCls} style={{ color: "#6F6A64" }}>Expiry date</label>
                            <input data-testid="cert-field-expiry" type="date" disabled={!d.has_expiry} value={d.expiry_date || ""} onChange={(e) => set("expiry_date", e.target.value)} className={inputCls + (d.has_expiry ? "" : " opacity-50")} />
                        </div>
                    </div>
                    <label className="flex items-center gap-2 text-[12px] text-[#2D2D2D]">
                        <input data-testid="cert-field-hasexpiry" type="checkbox" checked={!d.has_expiry} onChange={(e) => set("has_expiry", !e.target.checked)} />
                        This certificate has no expiry date
                    </label>
                    <div>
                        <label className={labelCls} style={{ color: "#6F6A64" }}>Scope / category covered</label>
                        <textarea data-testid="cert-field-scope" value={d.scope || ""} onChange={(e) => set("scope", e.target.value)} rows={2} className={inputCls} />
                    </div>
                    <div>
                        <label className={labelCls} style={{ color: "#6F6A64" }}>Notes</label>
                        <textarea data-testid="cert-field-notes" value={d.notes || ""} onChange={(e) => set("notes", e.target.value)} rows={2} className={inputCls} />
                    </div>

                    {/* File */}
                    <div className="flex items-center gap-2 p-2.5 rounded-[8px] border" style={{ borderColor: "#E5E0DA", background: "#FCFBF9" }}>
                        <FileText className="w-4 h-4" style={{ color: "#6F6A64" }} />
                        <span className="text-[12px] flex-1 truncate" style={{ color: d.file?.file_name ? "#1F1F1F" : "#9A968F" }}>
                            {d.file?.file_name || "No file attached"}
                        </span>
                        <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/*" className="hidden" data-testid="cert-attach-input" onChange={attachFile} />
                        <button onClick={() => fileRef.current?.click()} disabled={uploading}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-[7px] text-[11.5px] font-semibold border hover:bg-white disabled:opacity-60" style={{ borderColor: "#E5E0DA" }}>
                            {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                            {d.file?.file_name ? "Replace" : "Attach"}
                        </button>
                    </div>
                </div>

                <div className="flex items-center justify-end gap-2 px-5 py-3.5 border-t sticky bottom-0 bg-white" style={{ borderColor: "#F0EDE8" }}>
                    <button onClick={onClose} className="px-3.5 py-2 rounded-[8px] text-[12.5px] font-semibold border hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA" }}>Cancel</button>
                    <button data-testid="cert-save-button" onClick={save} disabled={saving}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[8px] text-[12.5px] font-semibold text-white disabled:opacity-60" style={{ background: ACCENT }}>
                        {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Save certificate
                    </button>
                </div>
            </div>
        </div>
    );
}
